import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc, Timestamp, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase-config';
import { Button } from '../components/ui/button';
import BrandingSettings from '../components/BrandingSettings';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import RequestsManagement from '../components/admin/RequestsManagement';
import RequestDetailModal from '../components/admin/RequestDetailModal';
import SubmitOnBehalfModal from '../components/admin/SubmitOnBehalfModal';
import SendGridSettings from '../components/admin/SendGridSettings';
import OnboardingWizard from '../components/admin/OnboardingWizard';
import TeamManagement from '../components/admin/TeamManagement';
import IntegrationsSettings from '../components/admin/IntegrationsSettings';
import { LogOut, Home, Building2, Loader2, Settings, BarChart3, List, UserPlus, Users, Plug, Sparkles } from 'lucide-react';

interface Customer {
  id: string;
  companyName: string;
  slug: string;
  domain: string;
  brandColor: string;
  accentColor?: string | null;
  plan: string;
  status: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  bannerTitle?: string | null;
  bannerDescription?: string | null;
  onboardingCompleted?: boolean;
  onboardingSkipped?: boolean;
}

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  voteCount: number;
  createdBy: string;
  createdByEmail: string;
  createdByNickname?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export default function CustomerAdminPortal() {
  const { user, signOut } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBrandingSettings, setShowBrandingSettings] = useState(false);
  const [showSendGridSettings, setShowSendGridSettings] = useState(false);
  const [showSubmitOnBehalf, setShowSubmitOnBehalf] = useState(false);
  const [activeView, setActiveView] = useState<'dashboard' | 'requests' | 'settings'>('dashboard');
  const [settingsTab, setSettingsTab] = useState<'team' | 'integrations' | 'branding'>('team');
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [requestIdProcessed, setRequestIdProcessed] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const fetchCustomer = async () => {
    if (!slug) {
      setError('No customer slug provided');
      setLoading(false);
      return;
    }

    try {
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, where('slug', '==', slug));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError('Customer not found');
        setLoading(false);
        return;
      }

      const customerData = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      } as Customer;

      setCustomer(customerData);

      // Check if onboarding should be shown
      if (!customerData.onboardingCompleted && !customerData.onboardingSkipped) {
        setShowOnboarding(true);
      }
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [slug]);

  // Check if user is super admin
  useEffect(() => {
    const checkSuperAdmin = async () => {
      if (!user) {
        setIsSuperAdmin(false);
        return;
      }

      try {
        const adminDoc = await getDoc(doc(db, 'admins', user.uid));
        setIsSuperAdmin(adminDoc.exists());
      } catch (error) {
        console.error('Error checking super admin status:', error);
        setIsSuperAdmin(false);
      }
    };

    checkSuperAdmin();
  }, [user]);

  // Handle deep linking to a specific request via ?request=ID query parameter
  useEffect(() => {
    const requestId = searchParams.get('request');

    // Only process if we have a requestId, customer is loaded, and we haven't already processed this ID
    if (requestId && customer && requestId !== requestIdProcessed) {
      console.log('Processing deep link for request:', requestId);

      // Fetch the specific request and open it
      const fetchAndOpenRequest = async () => {
        try {
          const requestDoc = await getDoc(doc(db, 'customers', customer.id, 'requests', requestId));
          if (requestDoc.exists()) {
            const requestData = {
              id: requestDoc.id,
              ...requestDoc.data(),
            } as FeatureRequest;

            console.log('Request found, opening modal:', requestData.title);

            // Switch to requests view and open the modal
            setActiveView('requests');
            setSelectedRequest(requestData);

            // Mark this request as processed
            setRequestIdProcessed(requestId);

            // Clear the query parameter after a short delay to ensure modal opens
            setTimeout(() => {
              setSearchParams({}, { replace: true });
            }, 100);
          } else {
            toast.error('Request not found');
            setRequestIdProcessed(requestId);
            setSearchParams({}, { replace: true });
          }
        } catch (error) {
          console.error('Error fetching request:', error);
          toast.error('Failed to load request');
          setRequestIdProcessed(requestId);
          setSearchParams({}, { replace: true });
        }
      };

      fetchAndOpenRequest();
    }
  }, [searchParams, customer, requestIdProcessed]);

  const handleStatusChange = async (requestId: string, newStatus: string) => {
    if (!customer) return;

    try {
      await updateDoc(doc(db, 'customers', customer.id, 'requests', requestId), {
        status: newStatus,
        updatedAt: Timestamp.now(),
      });

      // Refresh if there's a selected request
      if (selectedRequest && selectedRequest.id === requestId) {
        setSelectedRequest({
          ...selectedRequest,
          status: newStatus,
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading customer portal...</p>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-destructive">{error || 'Customer not found'}</p>
          <Link to="/admin">
            <Button>Back to Admin</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {customer.logoUrl ? (
                <div className="w-10 h-10 rounded border border-border flex items-center justify-center bg-white overflow-hidden">
                  <img
                    src={customer.logoUrl}
                    alt={`${customer.companyName} logo`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div
                  className="w-10 h-10 rounded flex items-center justify-center border border-border"
                  style={{ backgroundColor: customer.brandColor }}
                >
                  <Building2 className="h-5 w-5 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {customer.companyName}
                </h1>
                <p className="text-sm text-muted-foreground">
                  Customer Admin Portal
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden md:block text-sm text-muted-foreground">
                {user?.email}
              </div>
              <a
                href={`/${customer.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                View Portal
              </a>
              {isSuperAdmin && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm">
                    <Home className="h-4 w-4 mr-2" />
                    Super Admin
                  </Button>
                </Link>
              )}
              <Button variant="ghost" size="sm" onClick={signOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex gap-6">
              <button
                onClick={() => setActiveView('dashboard')}
                className={`px-4 py-4 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                  activeView === 'dashboard'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <BarChart3 className="h-4 w-4" />
                Dashboard
              </button>
              <button
                onClick={() => setActiveView('requests')}
                className={`px-4 py-4 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                  activeView === 'requests'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <List className="h-4 w-4" />
                Manage Requests
              </button>
              <button
                onClick={() => setActiveView('settings')}
                className={`px-4 py-4 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                  activeView === 'settings'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Settings className="h-4 w-4" />
                Settings
              </button>
            </div>
            <div className="py-2">
              <Button
                onClick={() => setShowSubmitOnBehalf(true)}
                style={{ backgroundColor: customer.brandColor }}
                className="text-white"
                size="sm"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Submit on Behalf
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeView === 'dashboard' && (
          <AnalyticsDashboard
            key={refreshKey}
            customerId={customer.id}
            brandColor={customer.brandColor}
            onViewRequest={setSelectedRequest}
          />
        )}

        {activeView === 'requests' && (
          <RequestsManagement
            key={refreshKey}
            customerId={customer.id}
            brandColor={customer.brandColor}
            onViewRequest={setSelectedRequest}
          />
        )}

        {activeView === 'settings' && (
          <div className="space-y-6">
            {/* Settings Sub-tabs */}
            <div className="border-b border-border">
              <div className="flex gap-6">
                <button
                  onClick={() => setSettingsTab('team')}
                  className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                    settingsTab === 'team'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Users className="h-4 w-4" />
                  Team
                </button>
                <button
                  onClick={() => setSettingsTab('integrations')}
                  className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                    settingsTab === 'integrations'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Plug className="h-4 w-4" />
                  Integrations
                </button>
                <button
                  onClick={() => setSettingsTab('branding')}
                  className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                    settingsTab === 'branding'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Settings className="h-4 w-4" />
                  Branding
                </button>
              </div>
            </div>

            {/* Settings Content */}
            {settingsTab === 'team' && (
              <TeamManagement
                customerId={customer.id}
                brandColor={customer.brandColor}
              />
            )}

            {settingsTab === 'integrations' && (
              <IntegrationsSettings
                customerId={customer.id}
                brandColor={customer.brandColor}
              />
            )}

            {settingsTab === 'branding' && customer && (
              <BrandingSettings
                customerId={customer.id}
                currentBrandColor={customer.brandColor}
                currentAccentColor={customer.accentColor}
                currentLogoUrl={customer.logoUrl}
                currentFaviconUrl={customer.faviconUrl}
                currentBannerTitle={customer.bannerTitle}
                currentBannerDescription={customer.bannerDescription}
                onUpdate={fetchCustomer}
                onClose={() => {}}
                isModal={false}
              />
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-muted-foreground">
            Powered by{' '}
            <a
              href="https://getproductworkshop.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium inline-flex items-center gap-1"
            >
              <Sparkles className="h-3.5 w-3.5" />
              ProductWorkshop
            </a>
          </div>
        </div>
      </footer>

      {/* Branding Settings Modal */}
      {showBrandingSettings && customer && (
        <BrandingSettings
          customerId={customer.id}
          currentBrandColor={customer.brandColor}
          currentAccentColor={customer.accentColor}
          currentLogoUrl={customer.logoUrl}
          currentFaviconUrl={customer.faviconUrl}
          currentBannerTitle={customer.bannerTitle}
          currentBannerDescription={customer.bannerDescription}
          onUpdate={fetchCustomer}
          onClose={() => setShowBrandingSettings(false)}
        />
      )}

      {/* SendGrid Settings Modal */}
      {showSendGridSettings && customer && (
        <SendGridSettings
          customerId={customer.id}
          currentApiKey={(customer as any).sendgridApiKey}
          currentFromEmail={(customer as any).sendgridFromEmail}
          currentFromName={(customer as any).sendgridFromName}
          onUpdate={fetchCustomer}
          onClose={() => setShowSendGridSettings(false)}
        />
      )}

      {/* Submit on Behalf Modal */}
      {showSubmitOnBehalf && customer && (
        <SubmitOnBehalfModal
          customerId={customer.id}
          brandColor={customer.brandColor}
          onClose={() => setShowSubmitOnBehalf(false)}
          onSuccess={() => {
            setShowSubmitOnBehalf(false);
            // Refresh the requests list
            setRefreshKey(prev => prev + 1);
          }}
        />
      )}

      {/* Request Detail Modal */}
      {selectedRequest && customer && (
        <RequestDetailModal
          request={selectedRequest}
          customerId={customer.id}
          customerSlug={customer.slug}
          brandColor={customer.brandColor}
          onClose={() => setSelectedRequest(null)}
          onStatusChange={handleStatusChange}
          onDelete={() => {
            // Close the modal and refresh the requests list
            setSelectedRequest(null);
            setRefreshKey(prev => prev + 1);
          }}
        />
      )}

      {/* Onboarding Wizard */}
      {showOnboarding && customer && (
        <OnboardingWizard
          customerId={customer.id}
          customerSlug={customer.slug}
          onComplete={() => {
            setShowOnboarding(false);
            fetchCustomer(); // Refresh to update onboarding status
          }}
          onSkip={() => {
            setShowOnboarding(false);
            fetchCustomer(); // Refresh to update onboarding status
          }}
        />
      )}
    </div>
  );
}
